import { Trans, useLingui } from '@lingui/react/macro'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { getErrorMessage } from '@tshared/oapi/req/models'
import { App, Button, Form, Input, Typography } from 'antd'

import { useMobileForgotPasswordMutation } from '@/apis/mobile-auth.api'
import { MailOutlined } from '@/icons'

import logo from '../-assets/logo.svg'
import { useFormValidation } from '../-hooks/useFormValidation'

const { Title } = Typography

export const Route = createFileRoute('/auth/forget-password/')({
  component: ForgetPasswordPage,
})

type ForgetPasswordFormValues = {
  email: string
}

function ForgetPasswordPage() {
  const { t } = useLingui()
  const { message } = App.useApp()
  const { getEmailRules } = useFormValidation()
  const forgotMutation = useMobileForgotPasswordMutation()
  const navigate = useNavigate()

  const onFinish = async (values: ForgetPasswordFormValues) => {
    try {
      await forgotMutation.mutateAsync({ email: values.email })
      void message.success(t`Reset link sent if the account exists`)
      void navigate({
        to: '/auth/success',
        search: {
          success_type: 'forget_password_success',
        },
      })
    } catch (error) {
      void message.error(
        getErrorMessage(error as Error, t`Failed to send reset link`) ??
          t`Failed to send reset link`,
      )
    }
  }

  return (
    <div style={{ width: '100%', maxWidth: '400px', margin: '0 auto', padding: '32px 0' }}>
      <div className="flex justify-center">
        <img src={logo} alt="" className="mb-8" />
      </div>
      <Title level={1} className="mb-1">
        <Trans>Forgot Password</Trans>
      </Title>
      <p className="mb-6 text-sm text-gray-500">
        <Trans>Enter your email and we will send you a password reset link</Trans>
      </p>

      <Form<ForgetPasswordFormValues> onFinish={onFinish} layout="vertical" size="large">
        <Form.Item name="email" rules={getEmailRules()}>
          <Input prefix={<MailOutlined />} placeholder={t`Enter email address`} />
        </Form.Item>

        <Form.Item style={{ marginTop: 24 }}>
          <Button
            type="primary"
            htmlType="submit"
            loading={forgotMutation.isPending}
            block
            size="large"
          >
            <Trans>Send Reset Link</Trans>
          </Button>
        </Form.Item>
      </Form>
    </div>
  )
}
